import Checkbox from "@cloudscape-design/components/checkbox"
import FormField from "@cloudscape-design/components/form-field"
import Input from "@cloudscape-design/components/input"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { UseFormSetValue, UseFormWatch, FieldErrors } from "react-hook-form"

import { ReadOnlyValue } from "../shared/ReadOnlyValue"
import { CollapsibleSection } from "./CollapsibleSection"
import { FieldMultiSelect } from "./FieldMultiSelect"
import { DataQualityFormData } from "./types"
import { MetadataField } from "./useMetadataFields"

/**
 * Props for the TableConfigSection component.
 */
interface TableConfigSectionProps {
  /**
   * React-hook-form setValue function for programmatic updates.
   */
  setValue: UseFormSetValue<DataQualityFormData>

  /**
   * React-hook-form watch function for observing field values.
   */
  watch: UseFormWatch<DataQualityFormData>

  /**
   * Whether the form is in edit mode.
   */
  canEdit: boolean

  /**
   * Whether the table name was fixed by an approved DTA version.
   */
  tableNameLocked?: boolean

  /**
   * Validation error returned by the save API for the table name.
   */
  serverTableNameError?: string

  /**
   * Clears a server-side table-name error after the author changes the value.
   */
  onTableNameChange?: () => void

  /**
   * Form validation errors.
   */
  errors?: FieldErrors<DataQualityFormData>

  /**
   * Available fields from metadata for dropdowns.
   */
  availableFields: MetadataField[]

  /**
   * Callback to navigate to the Metadata tab.
   */
  onNavigateToMetadata?: () => void
}

/**
 * Section component for Iceberg table configuration. Allows setting the table name and primary key
 * fields.
 */
export const TableConfigSection: React.FC<TableConfigSectionProps> = ({
  setValue,
  watch,
  canEdit,
  tableNameLocked = false,
  serverTableNameError,
  onTableNameChange,
  errors,
  availableFields,
  onNavigateToMetadata,
}) => {
  const primaryKeyFields = watch("table.primaryKeyFields") || []

  const handlePrimaryKeyFieldsChange = (fields: string[]) => {
    setValue("table.primaryKeyFields", fields, { shouldDirty: true })
  }

  const skipHeaderRows = watch("table.skipHeaderRows")

  return (
    <CollapsibleSection
      title="Table Configuration"
      description="Configure where validated data will be stored"
      commentAnchor="Data Quality Rules / Table Configuration"
    >
      <SpaceBetween size="m">
        <FormField
          label="Table Name"
          description={
            tableNameLocked
              ? "Locked: set when the DTA was approved."
              : "Generated when the DTA is created; editable until approval."
          }
          errorText={serverTableNameError || errors?.table?.name?.message}
          data-field="data_quality_rules.table.name"
        >
          {canEdit ? (
            <Input
              type="text"
              placeholder="ea_adjudication"
              value={watch("table.name") ?? ""}
              onChange={({ detail }) => {
                onTableNameChange?.()
                setValue("table.name", detail.value, { shouldDirty: true })
              }}
              disabled={tableNameLocked}
              invalid={!!serverTableNameError || !!errors?.table?.name}
            />
          ) : (
            <ReadOnlyValue value={watch("table.name")} />
          )}
        </FormField>

        <FormField
          label="Primary Key Fields"
          description="Derived from Unique Identifier checkboxes on the Metadata tab"
          errorText={errors?.table?.primaryKeyFields?.message}
          data-field="data_quality_rules.table.primaryKeyFields"
        >
          <FieldMultiSelect
            value={primaryKeyFields}
            onChange={handlePrimaryKeyFieldsChange}
            availableFields={availableFields}
            disabled={true}
            readOnly={!canEdit}
            placeholder="Set via Metadata tab"
            hasError={!!errors?.table?.primaryKeyFields}
            onNavigateToMetadata={onNavigateToMetadata}
          />
        </FormField>

        <FormField
          label="Skip Header Rows"
          description="Number of header rows to skip before data rows. Use 1 for 2-row header CSVs (e.g. file format where row 1 is labels and row 2 is variable names)."
        >
          {canEdit ? (
            <Input
              type="number"
              placeholder="0"
              value={skipHeaderRows != null ? String(skipHeaderRows) : ""}
              onChange={({ detail }) =>
                setValue(
                  "table.skipHeaderRows",
                  detail.value === "" ? undefined : Number(detail.value),
                  { shouldDirty: true },
                )
              }
            />
          ) : (
            <ReadOnlyValue value={skipHeaderRows} />
          )}
        </FormField>

        {canEdit ? (
          <Checkbox
            checked={!!watch("table.scd2.enabled")}
            onChange={({ detail }) =>
              setValue("table.scd2.enabled", detail.checked, { shouldDirty: true })
            }
            description="Track historical changes to records over time (recommended)"
          >
            Enable SCD-2 History Tracking
          </Checkbox>
        ) : (
          <FormField label="Enable SCD-2 History Tracking">
            <ReadOnlyValue value={watch("table.scd2.enabled") ? "Yes" : "No"} />
          </FormField>
        )}
      </SpaceBetween>
    </CollapsibleSection>
  )
}
